/* partify-universal-fitment.js
 * ---------------------------------------------------------------------------
 * Touch-up-paint template ONLY. Loaded via a custom-liquid block in
 * templates/product.touch-up-paint.json — no other product template loads it,
 * so behaviour elsewhere is unchanged.
 *
 * Makes the vehicle-collection's `universal_products` metafield the single
 * source of truth for the PDP fitment card. It wraps PartifyFitment.resolve()
 * so that, for a universal paint product, "fits" is decided by whether THIS
 * product is listed in the active vehicle's collection universal_products
 * (looked up by collection handle, fetched as JSON from the
 * collection.universal-json view). Match is by product ID — rename/handle proof.
 *
 * Requires (injected by the touch-up template):
 *   window.__PARTIFY_PRODUCT_ID  — numeric product id of the current product
 * ---------------------------------------------------------------------------
 */
(function () {
	"use strict";

	var PRODUCT_ID = Number(window.__PARTIFY_PRODUCT_ID || 0);
	if (!PRODUCT_ID) return; // not configured → no-op

	// ymmKey -> { state: 'loading' | 'ready', ids: [numbers] }
	var cache = {};

	function activeVehicle() {
		try {
			var a = JSON.parse(localStorage.getItem("searchTerms") || "[]");
			return Array.isArray(a) && a.length && a[0] && a[0].model ? a[0] : null;
		} catch (_) {
			return null;
		}
	}

	function toHandle(str) {
		if (!str) return "";
		return String(str)
			.toLowerCase()
			.replace(/[./+:]/g, "-")
			.replace(/\s+/g, "-")
			.replace(/[^\w-]+/g, "")
			.replace(/--+/g, "-")
			.replace(/-+$/, "");
	}

	// Candidate collection handles, specific → broad (mirrors the resolver).
	function candidates(v) {
		if (!v) return [];
		var raw = [];
		if (v.ymm) raw.push(v.ymm);
		var sub = [v.year, v.make, v.model, v.submodel].filter(Boolean).join(" ");
		if (sub) raw.push(sub);
		raw.push([v.year, v.make, v.model].filter(Boolean).join(" "));
		var seen = {},
			out = [];
		for (var i = 0; i < raw.length; i++) {
			var h = toHandle(raw[i]);
			if (h && !seen[h]) {
				seen[h] = 1;
				out.push(h);
			}
		}
		return out;
	}

	function ymmKey(v) {
		return v ? toHandle(v.ymm || [v.year, v.make, v.model].join(" ")) : "";
	}

	function fetchUniversal(v) {
		var key = ymmKey(v);
		if (!key || cache[key]) return; // already loading or ready
		cache[key] = { state: "loading", ids: [] };
		var handles = candidates(v);
		Promise.all(
			handles.map(function (h) {
				return fetch("/collections/" + h + "?view=universal-json", {
					credentials: "same-origin",
				})
					.then(function (r) {
						return r.ok ? r.text() : "[]";
					})
					.then(function (t) {
						try {
							return JSON.parse(t);
						} catch (_) {
							return [];
						}
					})
					.catch(function () {
						return [];
					});
			}),
		).then(function (lists) {
			var ids = {};
			lists.forEach(function (arr) {
				(arr || []).forEach(function (id) {
					ids[Number(id)] = 1;
				});
			});
			cache[key] = {
				state: "ready",
				ids: Object.keys(ids).map(Number),
			};
			refresh();
		});
	}

	// true / false / null(=still loading)
	function isUniversalFit(v) {
		var key = ymmKey(v);
		var entry = cache[key];
		if (!entry) {
			fetchUniversal(v);
			return null;
		}
		if (entry.state === "loading") return null;
		return entry.ids.indexOf(PRODUCT_ID) !== -1;
	}

	// Re-evaluate the card + ATC gate once async data lands.
	function refresh() {
		if (window.PartifyFitment && window.PartifyFitment.run) {
			try {
				window.PartifyFitment.run();
			} catch (_) {}
		}
		document.dispatchEvent(new CustomEvent("partify:vehicle:changed"));
	}

	// ── Wrap PartifyFitment.resolve so the metafield is authoritative ──
	function installWrap() {
		if (
			!window.PartifyFitment ||
			typeof window.PartifyFitment.resolve !== "function" ||
			window.PartifyFitment.__universalWrapped
		) {
			return !!(window.PartifyFitment && window.PartifyFitment.__universalWrapped);
		}
		var orig = window.PartifyFitment.resolve;
		window.PartifyFitment.resolve = function () {
			var base = orig.apply(this, arguments);
			if (!base || !base.vehicle) return base; // no vehicle → unchanged
			if (base.state === "success") return base; // already a real collection match
			var fit = isUniversalFit(base.vehicle);
			if (fit === null) return { state: "loading", vehicle: base.vehicle };
			if (fit)
				return {
					state: "success",
					vehicle: base.vehicle,
					matchLevel: "universal-metafield",
				};
			return { state: "fail", vehicle: base.vehicle };
		};
		window.PartifyFitment.__universalWrapped = true;
		return true;
	}

	function boot() {
		var v = activeVehicle();
		if (!installWrap()) {
			var tries = 0;
			var iv = setInterval(function () {
				if (installWrap() || ++tries > 50) {
					clearInterval(iv);
					var vv = activeVehicle();
					if (vv) {
						fetchUniversal(vv);
						refresh();
					}
				}
			}, 100);
			return;
		}
		if (v) {
			fetchUniversal(v);
			refresh();
		}
	}

	// Install the wrap as early as possible (resolver loads in <head>, before this
	// body script) so the card's first resolve() call already routes through it —
	// the card shows "Checking fitment…" then resolves, never a false "DOES NOT FIT".
	installWrap();

	window.addEventListener("searchTermsUpdated", function () {
		var v = activeVehicle();
		if (v) fetchUniversal(v);
	});
	document.addEventListener("partify:vehicle:changed", function () {
		var v = activeVehicle();
		if (v) fetchUniversal(v);
	});

	if (document.readyState === "loading")
		document.addEventListener("DOMContentLoaded", boot);
	else boot();
})();
